import React, { useState, useRef } from "react";
import { FormGroup, FormControl, ControlLabel, Button, Alert } from "react-bootstrap";
import { get, post } from 'aws-amplify/api';
import { parseCSV, parseJSON, exportToCSV, exportToJSON, downloadFile } from "../utils/importExport";
import utils from "../utils";
import "./ImportExport.css";

export default function ImportExport() {
  const fileInput = useRef(null);
  const [importName, setImportName] = useState("");
  const [fileType, setFileType] = useState("csv");
  const [isImporting, setIsImporting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [error, setError] = useState(null);

  async function handleImport(event) {
    event.preventDefault();
    
    if (!fileInput.current || !fileInput.current.files || fileInput.current.files.length === 0) {
      setError("Please select a file to import");
      return;
    }

    if (!importName.trim()) {
      setError("Please enter an import name (e.g., 'LastPass Export 2024')");
      return;
    }

    setIsImporting(true);
    setError(null);
    setImportResult(null);

    try {
      const file = fileInput.current.files[0];
      const content = await readFileContent(file);
      
      let records;
      if (fileType === 'csv') {
        records = parseCSV(content, importName.trim());
      } else {
        records = parseJSON(content, importName.trim());
      }

      if (records.length === 0) {
        throw new Error('No valid records found in the file');
      }

      // Import each record
      let successCount = 0;
      let failCount = 0;

      for (const record of records) {
        try {
          // Encrypt the data before sending to API
          const encryptedData = {
            websiteName: utils.encrypt(record.websiteName),
            loginId: utils.encrypt(record.loginId),
            password: utils.encrypt(record.password),
            websiteUrl: utils.encrypt(record.websiteUrl)
          };

          const restOperation = post({
            apiName: "logindetails",
            path: "/logindetails",
            options: {
              body: {
                data: encryptedData,
                importSource: record.importSource,
                importDate: record.importDate
              }
            }
          });
          await restOperation.response;
          successCount++;
        } catch (err) {
          console.error('Failed to import record:', record.websiteName, err);
          failCount++;
        }
      }

      setImportResult({
        total: records.length,
        success: successCount,
        failed: failCount
      });

      // Clear form
      setImportName("");
      if (fileInput.current) {
        fileInput.current.value = "";
      }
    } catch (err) {
      setError(err.message || 'Failed to import file');
      console.error('Import error:', err);
    } finally {
      setIsImporting(false);
    }
  }

  async function handleExport(format) {
    setIsExporting(true);
    setError(null);

    try {
      // Fetch all login details
      const restOperation = get({
        apiName: "logindetails",
        path: "/logindetails"
      });
      const { body } = await restOperation.response;
      const loginDetails = await body.json();

      let content, filename, mimeType;

      if (format === 'csv') {
        content = exportToCSV(loginDetails);
        filename = `password-manager-export-${Date.now()}.csv`;
        mimeType = 'text/csv';
      } else {
        content = exportToJSON(loginDetails);
        filename = `password-manager-export-${Date.now()}.json`;
        mimeType = 'application/json';
      }

      downloadFile(content, filename, mimeType);
    } catch (err) {
      setError('Failed to export: ' + err.message);
      console.error('Export error:', err);
    } finally {
      setIsExporting(false);
    }
  }

  function readFileContent(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = (e) => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  }

  return (
    <div className="ImportExport">
      <h2>Import / Export</h2>

      {error && (
        <Alert bsStyle="danger" onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}

      {importResult && (
        <Alert bsStyle="success" onDismiss={() => setImportResult(null)}>
          <strong>Import Complete!</strong>
          <ul>
            <li>Total records: {importResult.total}</li>
            <li>Successfully imported: {importResult.success}</li>
            {importResult.failed > 0 && <li>Failed: {importResult.failed}</li>}
          </ul>
        </Alert>
      )}

      <div className="import-section">
        <h3>📥 Import Passwords</h3>
        <p className="help-text">
          Import passwords from CSV or JSON files. Each imported item will be tagged with your import name 
          so you can easily find and delete them later if needed.
        </p>

        <form onSubmit={handleImport}>
          <FormGroup controlId="importName">
            <ControlLabel>Import Name (Required) *</ControlLabel>
            <FormControl
              type="text"
              value={importName}
              onChange={(e) => setImportName(e.target.value)}
              placeholder="e.g., LastPass Export 2024, Chrome Passwords"
              disabled={isImporting}
            />
            <p className="help-text">
              This name will be saved with each imported item so you can filter by it later
            </p>
          </FormGroup>

          <FormGroup controlId="fileType">
            <ControlLabel>File Format</ControlLabel>
            <FormControl
              componentClass="select"
              value={fileType}
              onChange={(e) => setFileType(e.target.value)}
              disabled={isImporting}
            >
              <option value="csv">CSV (Comma-Separated Values)</option>
              <option value="json">JSON</option>
            </FormControl>
          </FormGroup>

          <FormGroup controlId="file">
            <ControlLabel>Select File</ControlLabel>
            <FormControl
              type="file"
              accept={fileType === 'csv' ? '.csv' : '.json'}
              inputRef={ref => fileInput.current = ref}
              disabled={isImporting}
            />
          </FormGroup>

          <Button
            type="submit"
            bsStyle="primary"
            bsSize="large"
            disabled={isImporting}
          >
            {isImporting ? 'Importing...' : 'Import Passwords'}
          </Button>
        </form>

        <div className="format-help">
          <h4>Supported CSV Formats:</h4>
          <ul>
            <li><strong>LastPass:</strong> name, url, username, password</li>
            <li><strong>1Password:</strong> title, website, username, password</li>
            <li><strong>Chrome/Firefox:</strong> name, url, username, password</li>
            <li><strong>Generic:</strong> Any CSV with website, username, password columns</li>
          </ul>
        </div>
      </div>

      <hr />

      <div className="export-section">
        <h3>📤 Export Passwords</h3>
        <p className="help-text">
          Export all your passwords to CSV or JSON format. Keep the exported file secure!
        </p>

        <div className="export-buttons">
          <Button
            bsStyle="success"
            bsSize="large"
            onClick={() => handleExport('csv')}
            disabled={isExporting}
          >
            {isExporting ? 'Exporting...' : 'Export as CSV'}
          </Button>
          {' '}
          <Button
            bsStyle="success"
            bsSize="large"
            onClick={() => handleExport('json')}
            disabled={isExporting}
          >
            {isExporting ? 'Exporting...' : 'Export as JSON'}
          </Button>
        </div>

        <Alert bsStyle="warning" className="security-warning">
          <strong>⚠️ Security Warning:</strong> Exported files contain your passwords in plain text. 
          Store them securely and delete them after use.
        </Alert>
      </div>
    </div>
  );
}
